import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxChartComponent,
  DxChartModule,
  DxPivotGridComponent,
  DxPivotGridModule,
  DxSwitchModule,
  DxToolbarModule,
  DxPieChartModule,
  DxPieChartComponent,
} from 'devextreme-angular';
import { DataCoreService } from '../../services/data-core.service';
import { Options } from 'devextreme/ui/pivot_grid/data_source';
import { OrtakService } from '../../services/ortak.service';
import { map, Observable } from 'rxjs';
import {
  DlistDto,
  DlistFieldDto,
  GetLookup_Response,
} from '../../models/ortak.dtos';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../services/app-settings.service';
import { LookupModel } from '../../models/dx-data-table.model';
import { DxPivotGridTypes } from 'devextreme-angular/ui/pivot-grid';
import { Workbook } from 'exceljs';
import { exportPivotGrid } from 'devextreme/excel_exporter';
import * as saveAs from 'file-saver';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import {
  ContextMenuPreparingEvent,
  OptionChangedEvent,
  PivotGridDataFieldArea,
} from 'devextreme/ui/pivot_grid';
import {
  DxoCommonSeriesSettingsComponent,
  DxoCommonSeriesSettingsModule,
} from 'devextreme-angular/ui/nested';
import { PolarChartSeriesType } from 'devextreme/viz/polar_chart';
import DataSource from 'devextreme/data/data_source';
import { GenPivotGridService } from './gen-pivot-grid.service';

@Component({
  selector: 'dk-gen-pivot-grid',
  standalone: true,
  imports: [
    CommonModule,
    DxPivotGridModule,
    DxToolbarModule,
    DxButtonModule,
    DxSwitchModule,
    DxChartModule,
    DxPieChartModule,
  ],
  templateUrl: './gen-pivot-grid.component.html',
  styleUrls: ['./gen-pivot-grid.component.scss'],
})
export class GenPivotGridComponent {
  @ViewChild(DxPivotGridComponent, { static: false })
  pivotGrid!: DxPivotGridComponent;
  @ViewChild(DxChartComponent, { static: false }) chart!: DxChartComponent;
  @ViewChild('pieChart', { static: false }) pieChart!: DxPieChartComponent;

  dataSource: any;
  @Input() gridCtlList: DlistDto;
  @Input() pivotGridMode: boolean = false;
  @Input() gridDataSource: any;
  @Input() pivotLoadUrl: string;
  @Output() pivotGridModeChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  dataSourceConfig: Options;

  chartType: PolarChartSeriesType = null;

  constructor(
    private pivotGridService: GenPivotGridService,
    private httpClient: HttpClient,
    private ortakService: OrtakService,
    private dataCoreService: DataCoreService
  ) {}

  ngAfterViewInit() {
    this.pivotGrid.instance.bindChart(this.chart.instance, {
      dataFieldsDisplayMode: 'splitPanes',
      alternateDataFields: false,
    });
  }

  ngOnInit() {
    this.setPivotFields();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges['gridDataSource'].firstChange == false ) {
      this.pivotGridService.setPivotFieldToMap(this.gridCtlList?.dlistCode, this.pivotGrid.instance.getDataSource().fields());
      this.dataSourceConfig = {
        remoteOperations: true,
        filter: this.gridDataSource?._storeLoadOptions?.filter,
        store: this.pivotGridService.createCustomStore(
          this.pivotLoadUrl,
          this.gridCtlList?.baseEntity || this.gridCtlList?.dlistCode,
          this.gridDataSource
        ),
        fields: this.pivotGridService.getPivotFieldFromMap(
          this.gridCtlList?.dlistCode
        ),
      };
    }
  }

  ngOnDestroy() {
    this.pivotGridService.setPivotFieldToMap(
      this.gridCtlList?.dlistCode,
      this.pivotGrid.instance.getDataSource().fields()
    );
  }

  setPivotFields() {
    if (
      this.pivotGridService.getPivotFieldFromMap(this.gridCtlList?.dlistCode)
    ) {
      this.dataSourceConfig = {
        remoteOperations: true,
        filter: this.gridDataSource?._storeLoadOptions?.filter,
        store: this.createCustomStore(),
        fields: this.pivotGridService.getPivotFieldFromMap(
          this.gridCtlList?.dlistCode
        ),
      };
    } else {
      this.dataSourceConfig = {
        remoteOperations: true,
        filter: this.gridDataSource?._storeLoadOptions?.filter,
        store: this.pivotGridService.createCustomStore(
          this.pivotLoadUrl,
          this.gridCtlList?.baseEntity || this.gridCtlList?.dlistCode,
          this.gridDataSource
        ),
        fields: this.pivotGridService.setPivotFields(this.gridCtlList),
      };

      this.pivotGridService.setPivotFieldToMap(
        this.gridCtlList?.dlistCode,
        this.dataSourceConfig.fields
      );
    }
  }

  chartBtnClick(e: PolarChartSeriesType) {
    if (e == this.chartType) {
      this.chartType = null;
    } else {
      this.chartType = e;
    }
  }

  printChart() {
    this.chart.instance.print();
  }

  exportChart(exportFileType: string) {
    this.chart.instance.exportTo(
      this.gridCtlList?.dlistName || 'Grafik',
      exportFileType
    );
  }


  onExporting() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Pivot Tablo');

    exportPivotGrid({
      component: this.pivotGrid.instance,
      worksheet,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          (this.gridCtlList?.dlistName || 'Grafik') + '.xlsx'
        );
      });
    });
  }

  private createCustomStore() {
    const loadUrl = this.pivotLoadUrl
      ? environment.dataService + this.pivotLoadUrl
      : environment.dataService + (this.gridCtlList.baseEntity || this.gridCtlList.dlistCode);
    const key = this.gridDataSource.store().key;
    const loadParamSelect = JSON.stringify(
      this.gridDataSource?._storeLoadOptions?.select
    );

    return createStore({
      key: key,
      loadUrl: loadUrl,
      loadParams: {
        select: loadParamSelect,
      },
      onBeforeSend: (method, ajaxOptions) => {
        ajaxOptions.contentType = 'application/json';
        // DevExtreme createStore istekleri HttpInterceptor'ı bypass eder,
        // bu yüzden token'ı burada merkezi helper ile ekliyoruz.
        this.dataCoreService.appendAuthHeader(ajaxOptions);
      },
    });
  }
}
